/* global axios */
/* eslint-disable no-console */
import CacheEnabledApiClient from './CacheEnabledApiClient';

class CampaignsAPI extends CacheEnabledApiClient {
  constructor() {
    super('campaigns', { accountScoped: true });
  }

  // eslint-disable-next-line class-methods-use-this
  get cacheModelName() {
    return 'campaigns';
  }

  getWithSchedules() {
    return axios.get(`${this.url}?include_schedules=true`);
  }

  // Builder node-based: manda o GRAFO cru {nodes, edges} — validação e compile
  // acontecem no backend (padrão Studio). dryRun=true só valida/compila,
  // sem criar campanha (sem chamador na UI hoje; contrato do backend).
  createFromBuilder(graph, { dryRun = false } = {}) {
    return axios.post(`${this.url}/create_from_builder`, {
      graph,
      dry_run: dryRun,
    });
  }

  // Grafo cru persistido na criação via builder — alimenta a aba Fluxo do
  // drawer de agendamentos. builder_graph=null quando a campanha foi criada
  // sem o builder (ou antes do registro).
  getBuilderGraph(campaignId) {
    return axios.get(`${this.url}/${campaignId}/builder_graph`);
  }

  deleteScheduling(campaignId, schedulingId) {
    return axios.delete(
      `${this.url}/${campaignId}/delete_scheduling/${schedulingId}`
    );
  }

  // Exclui os agendamentos pendentes DESTA campanha — o que já rodou é
  // história e o backend nunca toca. Devolve { deleted_count } real.
  deletePendingSchedules(campaignId) {
    return axios.delete(`${this.url}/${campaignId}/schedules/pending`);
  }

  async startCampaign(campaignId, schedulingId) {
    try {
      const response = await axios.post(
        `${this.url}/${campaignId}/schedules/${schedulingId}/start`
      );

      return response;
    } catch (error) {
      console.error('❌ [CampaignsAPI] startCampaign erro capturado:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        config: {
          url: error.config?.url,
          method: error.config?.method,
          headers: error.config?.headers,
        },
      });

      const errorMessage =
        error.response?.data?.message || 'Erro ao iniciar campanha';
      console.error('🔥 [CampaignsAPI] Lançando erro:', errorMessage);

      throw new Error(errorMessage);
    }
  }

  async stopCampaign(campaignId, schedulingId) {
    try {
      const response = await axios.post(
        `${this.url}/${campaignId}/schedules/${schedulingId}/stop`
      );

      return response;
    } catch (error) {
      console.error('❌ [CampaignsAPI] stopCampaign erro:', {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data,
      });

      // CORREÇÃO: Aqui estava "Erro ao iniciar campanha" mas deveria ser "parar"
      throw new Error(
        error.response?.data?.message || 'Erro ao parar campanha'
      );
    }
  }

  async listCampaignScheduleDeliveries(campaignId, schedulingId, params = {}) {
    try {
      const queryParams = new URLSearchParams();

      if (params.page) queryParams.append('page', params.page);
      if (params.per_page) queryParams.append('per_page', params.per_page);
      if (params.search) queryParams.append('search', params.search);
      if (params.status) {
        // Suporta array de status
        const statuses = Array.isArray(params.status)
          ? params.status
          : [params.status];
        statuses.forEach(s => queryParams.append('status[]', s));
      }

      const queryString = queryParams.toString();
      const url = `${this.url}/${campaignId}/schedules/${schedulingId}/deliveries${queryString ? `?${queryString}` : ''}`;

      const response = await axios.get(url);
      return response;
    } catch (error) {
      console.error(
        '❌ [CampaignsAPI] Erro ao listar entregas:',
        error.response?.data
      );
      throw new Error(
        error.response?.data?.error || 'Erro ao buscar entregas do agendamento'
      );
    }
  }

  async listCampaignErrorLogs(campaignId, schedulingId, params = {}) {
    try {
      const queryParams = new URLSearchParams();

      if (params.page) queryParams.append('page', params.page);
      if (params.per_page) queryParams.append('per_page', params.per_page);
      if (params.error_category)
        queryParams.append('error_category', params.error_category);
      if (params.error_type)
        queryParams.append('error_type', params.error_type);
      if (params.batch_number)
        queryParams.append('batch_number', params.batch_number);
      if (params.exclude_error_type)
        queryParams.append('exclude_error_type', params.exclude_error_type);
      if (params.include_filtered)
        queryParams.append('include_filtered', params.include_filtered);
      if (params.include_internal)
        queryParams.append('include_internal', params.include_internal);

      const url = `${this.url}/${campaignId}/schedules/${schedulingId}/error_logs${queryParams.toString() ? '?' + queryParams.toString() : ''}`;

      const response = await axios.get(url);
      return response;
    } catch (error) {
      console.error(
        '❌ [CampaignsAPI] Erro ao listar logs de erro:',
        error.response?.data
      );
      throw new Error(
        error.response?.data?.error || 'Erro ao buscar logs de erro'
      );
    }
  }

  async previewAudience(audience, limit = 200) {
    try {
      const payload = {
        audience: audience,
        limit: limit,
      };

      const response = await axios.post(
        `${this.url}/preview_audience`,
        payload
      );

      return response;
    } catch (error) {
      console.error('❌ [CampaignsAPI] Erro no preview:', error.response?.data);
      throw new Error(
        error.response?.data?.error || 'Erro ao fazer preview da audiência'
      );
    }
  }

  // NOVO: Listar todos os schedules com paginação server-side
  async listAllSchedules(params = {}) {
    try {
      const queryParams = new URLSearchParams();

      if (params.page) queryParams.append('page', params.page);
      if (params.per_page) queryParams.append('per_page', params.per_page);
      if (params.search) queryParams.append('search', params.search);
      if (params.channel_type)
        queryParams.append('channel_type', params.channel_type);
      if (params.campaign_type)
        queryParams.append('campaign_type', params.campaign_type);
      if (params.start_date)
        queryParams.append('start_date', params.start_date);
      if (params.end_date) queryParams.append('end_date', params.end_date);
      if (params.status) {
        // Suporta array de status
        const statuses = Array.isArray(params.status)
          ? params.status
          : [params.status];
        statuses.forEach(s => queryParams.append('status[]', s));
      }

      const queryString = queryParams.toString();
      const url = `${this.url}/list_all_schedules${queryString ? `?${queryString}` : ''}`;

      const response = await axios.get(url);
      return response;
    } catch (error) {
      throw new Error(
        error.response?.data?.error || 'Erro ao buscar schedules'
      );
    }
  }
}

export default new CampaignsAPI();
